import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { McpSectionNavHeader } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/section-nav-header";
import {
  McpServerCard,
  type McpServerCardSelectionState,
} from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/server-card";
import { McpToolCard } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/tool-card";
import { McpToolkitGroupHeader } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/toolkit-group-header";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Globe, Trash2, Users } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { useDeleteCatalogEntries } from "../api/hooks";
import { CATALOG_SUBJECT_EVERYONE, type ToolCatalogEntry } from "../types";
import { groupBySubjectThenToolkit, toolNameOf } from "../utils";

const audienceLabel = (subject: string): string =>
  subject === CATALOG_SUBJECT_EVERYONE ? "Everyone" : subject;

const AudienceBadge = ({ subject }: { subject: string }) =>
  subject === CATALOG_SUBJECT_EVERYONE ? (
    <Badge variant="secondary">Everyone</Badge>
  ) : (
    <Badge variant="outline">{subject}</Badge>
  );

const selectionState = (
  marked: number,
  total: number
): McpServerCardSelectionState => {
  if (marked === 0) {
    return "none";
  }
  if (marked === total) {
    return "selected";
  }
  return "partial";
};

// Cards/tools staged for removal read as destructive (red), not brand-green —
// green is the add/connect flow; this surface removes access.
const removeAccent = (state: McpServerCardSelectionState): string => {
  if (state === "selected") {
    return "border-destructive/60 bg-destructive/5 ring-1 ring-destructive/25";
  }
  if (state === "partial") {
    return "border-destructive/50 bg-destructive/5";
  }
  return "";
};

type Drill = { subject: string; toolkit: string };

export const CatalogAudienceManager = ({
  entries,
}: {
  entries: ToolCatalogEntry[];
}) => {
  const [staged, setStaged] = useState<Set<string>>(new Set());
  const [drill, setDrill] = useState<Drill | null>(null);
  const { mutate: deleteEntries, isPending } = useDeleteCatalogEntries();

  const audiences = useMemo(
    () => groupBySubjectThenToolkit(entries),
    [entries]
  );

  const stageMany = useCallback((ids: string[], checked: boolean) => {
    setStaged((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return next;
    });
  }, []);

  const removeIds = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        return;
      }
      deleteEntries(ids, {
        onSuccess: () =>
          setStaged((prev) => {
            const next = new Set(prev);
            for (const id of ids) {
              next.delete(id);
            }
            return next;
          }),
      });
    },
    [deleteEntries]
  );

  const stagedIds = useMemo(() => [...staged], [staged]);

  const drillToolkit = useMemo(() => {
    if (!drill) {
      return null;
    }
    return (
      audiences
        .find((audience) => audience.subject === drill.subject)
        ?.toolkits.find((toolkit) => toolkit.toolkit === drill.toolkit) ?? null
    );
  }, [audiences, drill]);

  if (drill && drillToolkit) {
    const ids = drillToolkit.entries.map((entry) => entry.id);
    const markedInToolkit = ids.filter((id) => staged.has(id)).length;

    return (
      <div className="space-y-4">
        <McpSectionNavHeader
          actions={
            <>
              <Button
                onClick={() => stageMany(ids, true)}
                size="sm"
                variant="ghost"
              >
                Select all
              </Button>
              <Button
                onClick={() => stageMany(ids, false)}
                size="sm"
                variant="ghost"
              >
                Clear
              </Button>
              <CanProject permission={PROJECT.catalog.delete}>
                <Button
                  disabled={isPending}
                  onClick={() => removeIds(ids)}
                  size="sm"
                  variant="destructive"
                >
                  <Trash2 className="mr-2 size-4" />
                  Remove all
                </Button>
              </CanProject>
            </>
          }
          backLabel="All audiences"
          identity={
            <div className="flex items-center gap-2">
              <McpToolkitGroupHeader
                count={`${drillToolkit.entries.length} tools`}
                name={drillToolkit.toolkit}
                toolkitId={drillToolkit.toolkit}
              />
              <AudienceBadge subject={drill.subject} />
            </div>
          }
          onBack={() => setDrill(null)}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          {drillToolkit.entries.map((entry) => {
            const isStaged = staged.has(entry.id);
            return (
              <McpToolCard
                className={cn(
                  isStaged &&
                    "border-destructive/60 bg-destructive/5 ring-1 ring-destructive/25"
                )}
                key={entry.id}
                name={toolNameOf(entry.tool_ref)}
                onToggle={() => stageMany([entry.id], !isStaged)}
                rawName={entry.tool_ref}
                selected={isStaged}
              />
            );
          })}
        </div>
        {markedInToolkit > 0 ? (
          <DeleteBar
            count={markedInToolkit}
            disabled={isPending}
            onClear={() => stageMany(ids, false)}
            onDelete={() => removeIds(ids.filter((id) => staged.has(id)))}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {audiences.map((audience) => (
        <section className="space-y-3" key={audience.subject}>
          <div className="flex items-center justify-between gap-3 border-b pb-3">
            <div className="flex items-center gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-muted text-muted-foreground">
                {audience.subject === CATALOG_SUBJECT_EVERYONE ? (
                  <Globe className="size-4" />
                ) : (
                  <Users className="size-4" />
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-base">
                    {audienceLabel(audience.subject)}
                  </span>
                  <Badge
                    variant={
                      audience.subject === CATALOG_SUBJECT_EVERYONE
                        ? "secondary"
                        : "outline"
                    }
                  >
                    {audience.subject === CATALOG_SUBJECT_EVERYONE
                      ? "All end-users"
                      : "IdP group"}
                  </Badge>
                </div>
                <span className="text-muted-foreground text-xs">
                  {audience.total} tool{audience.total === 1 ? "" : "s"} across{" "}
                  {audience.toolkits.length} server
                  {audience.toolkits.length === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            <CanProject permission={PROJECT.catalog.delete}>
              <Button
                disabled={isPending}
                onClick={() =>
                  removeIds(
                    audience.toolkits.flatMap((toolkit) =>
                      toolkit.entries.map((entry) => entry.id)
                    )
                  )
                }
                size="sm"
                variant="ghost"
              >
                <Trash2 className="mr-2 size-4" />
                Remove all
              </Button>
            </CanProject>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {audience.toolkits.map((toolkit) => {
              const ids = toolkit.entries.map((entry) => entry.id);
              const marked = ids.filter((id) => staged.has(id)).length;
              const state = selectionState(marked, ids.length);
              return (
                <McpServerCard
                  className={removeAccent(state)}
                  key={toolkit.toolkit}
                  name={toolkit.toolkit}
                  onCustomize={() =>
                    setDrill({
                      subject: audience.subject,
                      toolkit: toolkit.toolkit,
                    })
                  }
                  onToggle={() => stageMany(ids, state !== "selected")}
                  selectedToolCount={marked}
                  selectionState={state}
                  toolCount={ids.length}
                  toolkitId={toolkit.toolkit}
                />
              );
            })}
          </div>
        </section>
      ))}

      {stagedIds.length > 0 ? (
        <DeleteBar
          count={stagedIds.length}
          disabled={isPending}
          onClear={() => setStaged(new Set())}
          onDelete={() => removeIds(stagedIds)}
        />
      ) : null}
    </div>
  );
};

const DeleteBar = ({
  count,
  disabled,
  onClear,
  onDelete,
}: {
  count: number;
  disabled: boolean;
  onClear: () => void;
  onDelete: () => void;
}) => (
  <div className="sticky bottom-4 z-10 flex items-center justify-between gap-3 rounded-xl border border-destructive/40 bg-card/95 px-4 py-3 shadow-lg ring-1 ring-destructive/15 backdrop-blur">
    <span className="text-muted-foreground text-sm">
      {count} tool{count === 1 ? "" : "s"} staged for removal
    </span>
    <div className="flex items-center gap-2">
      <Button onClick={onClear} size="sm" variant="ghost">
        Clear
      </Button>
      <CanProject permission={PROJECT.catalog.delete}>
        <Button
          disabled={disabled}
          onClick={onDelete}
          size="sm"
          variant="destructive"
        >
          <Trash2 className="mr-2 size-4" />
          Delete selected ({count})
        </Button>
      </CanProject>
    </div>
  </div>
);
